import { Card, CardContent } from "@/components/ui/card"
import { TrendingUp, Sprout, Percent } from "lucide-react"
import { useCurrency } from "@/context/currency/currency-context.ts"
import { cn } from "@/lib/utils"
import type { SafetyDistribution } from "@/lib/portfolio-math"

interface PortfolioSummaryProps {
	totalValue: number
	annualIncome: number
	portfolioYield: number
	avgDividendGrowth: number
	yieldOnCost: number
	safetyDistribution?: SafetyDistribution
}

export function PortfolioSummary({
	annualIncome,
	portfolioYield,
	avgDividendGrowth,
	yieldOnCost,
	safetyDistribution,
}: PortfolioSummaryProps) {
	const { format } = useCurrency()

	return (
		<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
			<CardContent className="p-5 space-y-4">
				{/* Header */}
				<p className="text-sm font-semibold text-sky-950 dark:text-sky-50">Dividend Forecast</p>

				{/* Hero: Annual Income */}
				<div>
					<p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">
						Est. Annual Income
					</p>
					<p className="text-3xl font-extrabold text-sky-900 dark:text-sky-100 tabular-nums">
						{format(annualIncome, { skipConversion: true })}
					</p>
				</div>

				{/* Divider */}
				<div className="border-t border-sky-900/8 dark:border-sky-100/8" />

				{/* Stats row */}
				<div className="grid grid-cols-3">
					<div className="flex flex-col gap-1">
						<div className="flex items-center gap-1.5 h-8">
							<TrendingUp className="w-6 h-6 text-sky-500 dark:text-sky-400 shrink-0" />
							<span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
								Yield
							</span>
						</div>
						<p className="text-lg font-bold text-sky-900 dark:text-sky-100 tabular-nums">
							{(portfolioYield || 0).toFixed(2)}%
						</p>
					</div>

					<div className="flex flex-col gap-1 border-x border-sky-900/8 dark:border-sky-100/8 px-3">
						<div className="flex items-center gap-1.5 h-8">
							<Percent className="w-6 h-6 text-sky-500 dark:text-sky-400 shrink-0" />
							<span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
								YoC
							</span>
						</div>
						<p className="text-lg font-bold text-sky-900 dark:text-sky-100 tabular-nums">
							{(yieldOnCost || 0).toFixed(2)}%
						</p>
					</div>

					<div className="flex flex-col gap-1 pl-3">
						<div className="flex items-center gap-1.5 h-8">
							<Sprout className="w-6 h-6 text-sky-500 dark:text-sky-400 shrink-0" />
							<span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
								5Y Growth
							</span>
						</div>
						<p
							className={cn(
								"text-lg font-bold tabular-nums",
								avgDividendGrowth > 0
									? "text-sky-900 dark:text-sky-100"
									: avgDividendGrowth < 0
										? "text-red-600 dark:text-red-400"
										: "text-sky-900/50 dark:text-sky-100/50",
							)}
						>
							{(avgDividendGrowth || 0).toFixed(2)}%
						</p>
					</div>
				</div>

				{safetyDistribution &&
					safetyDistribution.green + safetyDistribution.amber + safetyDistribution.red > 0 && (
						<>
							<div className="border-t border-sky-900/8 dark:border-sky-100/8" />
							<div>
								<p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-2">
									Safety Distribution
								</p>
								{safetyDistribution.avgScore != null && (
									<p className="text-lg font-bold text-sky-900 dark:text-sky-100 tabular-nums mb-1">
										{safetyDistribution.avgScore}
										<span className="text-sm font-normal text-muted-foreground">/100</span>
									</p>
								)}
								<div className="flex gap-3 text-xs">
									{safetyDistribution.green > 0 && (
										<span className="flex items-center gap-1">
											<span className="w-2 h-2 rounded-full bg-green-500" />
											<span className="text-muted-foreground">{safetyDistribution.green}</span>
										</span>
									)}
									{safetyDistribution.amber > 0 && (
										<span className="flex items-center gap-1">
											<span className="w-2 h-2 rounded-full bg-amber-500" />
											<span className="text-muted-foreground">{safetyDistribution.amber}</span>
										</span>
									)}
									{safetyDistribution.red > 0 && (
										<span className="flex items-center gap-1">
											<span className="w-2 h-2 rounded-full bg-red-500" />
											<span className="text-muted-foreground">{safetyDistribution.red}</span>
										</span>
									)}
									{safetyDistribution.gray > 0 && (
										<span className="flex items-center gap-1">
											<span className="w-2 h-2 rounded-full bg-muted-foreground/30" />
											<span className="text-muted-foreground">{safetyDistribution.gray}</span>
										</span>
									)}
								</div>
							</div>
						</>
					)}
			</CardContent>
		</Card>
	)
}
